/* Packages */
import React from "react";
import { render } from "react-dom";
import { Provider } from "react-redux";
import thunkMiddleware from "redux-thunk";
import { createStore, applyMiddleware, compose } from "redux";
import { useLanguage } from "./_hooks/useLanguage";
import { normalizeLocale, readLocaleCookie, writeLocaleCookie } from "../i18n/locale";
import { PostHogProvider } from 'posthog-js/react';


// Internacionalization
import "../i18n/config";

/* Styles */
import "aphrodite-ui/dist/assets/aphrodite.css";
import "../assets/scss/app.scss";

/* App Router */
import AppRouter from "./router.jsx";

/* Redux Reducers */
import Reducers from "./_reducers/index.js";

/* Store Middlewares */
const middlewares = [thunkMiddleware];

/* URL Search Params API */
require("url-search-params-polyfill");

/* MKT-486 — this app deliberately does NOT bootstrap the analytics SDK.
 *
 * It runs embedded in an iframe, and `bootstrap()` ALWAYS loads a GTM container
 * ("GTM is the uniform default: always loaded" — awa-analytics-web/src/bootstrap.ts).
 * The SDK's embedded detection only suppresses the consent BANNER, not GTM, so
 * booting here created a SECOND container inside the iframe: events landed in the
 * iframe's own dataLayer, with `page_location` = auth.ingresse.com and a GA4
 * session detached from the host's. `login`/`sign_up` are therefore emitted by the
 * HOST (site_next / alchemist), which owns the top-level container; this app only
 * NOTIFIES it via `postMessageService.authLoginSuccess/authRegisterSuccess`.
 */

/* Development Logger */
if (process.env.NODE_ENV === "development") {
    const { logger } = require("redux-logger");

    middlewares.push(logger);
}

const currentLanguage = useLanguage();
const isBackstage = window.location.href.includes("backstage");

// Backstage is not offered in English. When a stale en-US cookie arrives from
// another application, rewrite it with what `useLanguage` resolved instead —
// otherwise the next application to read the cookie gets English back.
// `writeLocaleCookie` is what keeps `secure`/`SameSite=Lax` on it; the inline
// write this replaces dropped both and silently downgraded the shared cookie.
if (isBackstage && normalizeLocale(readLocaleCookie()) === "en-US") {
    writeLocaleCookie(currentLanguage);
} else if (!normalizeLocale(readLocaleCookie())) {
    /*
     * No usable preference stored yet — persist whatever was resolved (a
     * `?locale=` from the application that sent the visitor here, or the
     * default). Without this the language would have to be re-sent on every
     * hop, and the next Ingresse application would open in the default again.
     */
    writeLocaleCookie(currentLanguage);
}

/* Enhancers */
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

/* Redux Store */
const store = createStore(
    Reducers,
    composeEnhancers(applyMiddleware(...middlewares))
);

const options = {
    api_host : process.env.REACT_APP_POSTHOG_HOST
};

/* App Render */
render(
    <Provider store={store}>
        <PostHogProvider apiKey={process.env.REACT_APP_POSTHOG_KEY} options={options}>
            <AppRouter />
        </PostHogProvider>
    </Provider>,
    document.getElementById("root")
);
